import { Tooltip } from "components/general/Tooltip";
import { PropsWithChildren } from "react";
import { GlobalStore } from "store/global/GlobalStore";
import { getWrappedTokenAddress } from "utils/chainUtils";
import { TokenRowDisabledReason } from "utils/tokenSelectorUtils";
import {
  AllOtherTokensDisabledHoverContent,
  OpposingPartyNon121TooltipContent,
  SamePartyTooltipContent,
} from "./SelectTokenModalTooltipContents";
import { CounterParty } from "utils/tradeUtils";
import { Hash } from "viem";
import { NATIVE_TOKEN_ADDRESS } from "config/consts";
import { isAddressEqual } from "../../../utils/tokenUtils";

interface TooltipProps {
  counterParty: CounterParty;
  tokenAddress: Hash;
  tokenDisabledReason: TokenRowDisabledReason | null;
}

export const SelectTokenRowTooltip = ({
  counterParty,
  tokenAddress,
  tokenDisabledReason,
  children,
}: PropsWithChildren<TooltipProps>) => {
  const network = GlobalStore.useState(s => s.network);
  const wrappedTokenAddress = getWrappedTokenAddress(network);
  const isDisabled = tokenDisabledReason != null;

  if (!isDisabled) {
    return <>{children}</>;
  }

  const TooltipContent = () => {
    const isTaker = counterParty === "taker";
    if (tokenDisabledReason === TokenRowDisabledReason.IsWrapTrade) {
      return (
        <AllOtherTokensDisabledHoverContent
          nativeToken={NATIVE_TOKEN_ADDRESS}
          wrappedToken={wrappedTokenAddress}
          isTaker={isTaker}
        />
      );
    }

    if (tokenAddress !== wrappedTokenAddress && tokenAddress !== NATIVE_TOKEN_ADDRESS) {
      return null;
    }

    const oppositeSymbol = isAddressEqual(tokenAddress, NATIVE_TOKEN_ADDRESS)
      ? wrappedTokenAddress
      : NATIVE_TOKEN_ADDRESS;

    if (tokenDisabledReason === TokenRowDisabledReason.SelectedTokensAlreadyIncludesNatives) {
      return (
        <SamePartyTooltipContent
          currentTokenAddress={tokenAddress}
          otherTokenAddress={oppositeSymbol}
          isTaker={isTaker}
        />
      );
    }

    if (tokenDisabledReason === TokenRowDisabledReason.OppositePartyHasNativesAndIsNon121Trade) {
      return (
        <OpposingPartyNon121TooltipContent
          toPay={isTaker ? tokenAddress : oppositeSymbol}
          toReceive={isTaker ? oppositeSymbol : tokenAddress}
          isTaker={isTaker}
        />
      );
    }

    return null;
  };

  if (TooltipContent() == null) {
    return <>{children}</>;
  }

  return (
    <Tooltip placement="top" title={TooltipContent}>
      {children}
    </Tooltip>
  );
};
